/**
 * Intro word sequences for the BuildUpSequence.
 *
 * Each sequence is 4 short beats, written for a specific emotional tone.
 * The picker uses the seeded rng so two announcements in the same tone can
 * still land on different opening lines — recipients should never predict
 * what the first beat will be.
 */

import type { EmotionalTone } from './emotionalSignals';
import type { SeededRng } from './revealSeed';

export interface IntroSequence {
  id: string;
  tone: EmotionalTone | 'universal';
  beats: [string, string, string, string];
}

const BANK: IntroSequence[] = [
  // universal — always available
  { id: 'u_someone',  tone: 'universal',   beats: ['Someone took time…', 'Not after loss.', 'Right now.', 'Open your heart.'] },
  { id: 'u_pause',    tone: 'universal',   beats: ['Pause for a moment…', 'Breathe.', "Something's waiting.", 'Just for you.'] },
  { id: 'u_listen',   tone: 'universal',   beats: ['Before you scroll on…', 'Stay here.', 'Listen with your heart.', "It's beginning."] },

  // warm
  { id: 'w_thinking', tone: 'warm',        beats: ['They thought of you today.', 'Not by accident.', 'On purpose.', 'Read slowly.'] },
  { id: 'w_quiet',    tone: 'warm',        beats: ['A quiet moment…', 'A few words.', 'Held just for you.', 'Open it gently.'] },

  // reflective
  { id: 'r_remember', tone: 'reflective',  beats: ['Some words…', 'take years to arrive.', "These found their moment.", 'Sit with them.'] },
  { id: 'r_lookback', tone: 'reflective',  beats: ['Looking back…', 'Some things stay.', 'This is one of them.', 'Take your time.'] },

  // celebratory
  { id: 'c_today',    tone: 'celebratory', beats: ['Today is yours.', 'Someone made sure.', "Here's why.", "Let's celebrate."] },
  { id: 'c_pulse',    tone: 'celebratory', beats: ['A heartbeat away…', 'Pure joy.', 'Made for you.', 'Open it.'] },

  // apologetic
  { id: 'a_long',     tone: 'apologetic',  beats: ['Some words…', 'wait too long.', 'Not these.', 'Receive them.'] },
  { id: 'a_brave',    tone: 'apologetic',  beats: ['It took courage…', 'to write this.', 'It takes care…', 'to read it.'] },

  // romantic
  { id: 'rm_heart',   tone: 'romantic',    beats: ['One heart to another.', 'Across everything.', 'Always you.', 'Listen close.'] },
  { id: 'rm_chose',   tone: 'romantic',    beats: ['They chose you.', 'Today.', 'Again.', 'Read every word.'] },

  // closure
  { id: 'cl_peace',   tone: 'closure',     beats: ['Peace asked to speak.', 'Through these words.', "Let it land.", 'You are held.'] },
  { id: 'cl_breath',  tone: 'closure',     beats: ['One last breath…', 'before the page turns.', 'This is it.', 'Receive it.'] },

  // legacy
  { id: 'l_carry',    tone: 'legacy',      beats: ['Some things outlast us.', 'This was meant to.', 'For you.', 'For after.'] },
  { id: 'l_seed',     tone: 'legacy',      beats: ['A seed planted today…', 'will grow without us.', 'This is the seed.', 'Open it.'] },

  // gratitude
  { id: 'g_thank',    tone: 'gratitude',   beats: ['Some thanks…', 'arrive too late.', 'This one is in time.', 'Read it slowly.'] },
  { id: 'g_owe',      tone: 'gratitude',   beats: ['You may not know…', 'what you gave.', 'They want you to.', 'Listen.'] },

  // healing
  { id: 'h_gentle',   tone: 'healing',     beats: ['Soft words ahead.', 'For a soft moment.', 'Just be here.', "It's beginning."] },
  { id: 'h_courage',  tone: 'healing',     beats: ['You are not alone.', 'Someone made sure.', 'Read this gently.', "It's for you."] },
];

export function pickIntroSequence(tone: EmotionalTone | undefined, rng: SeededRng): IntroSequence {
  const matched = BANK.filter((s) => s.tone === tone);
  const universal = BANK.filter((s) => s.tone === 'universal');
  const pool = matched.length ? [...matched, ...matched, ...universal] : universal;
  return rng.pick(pool);
}

export function allIntroSequences(): IntroSequence[] {
  return BANK;
}
